import { ThemeEvents } from '@theme/events';
import { formatCentsIntl } from '@theme/utilities';

/* ------------------------------ event wiring ------------------------------ */
document.addEventListener('promo-discount-ready', (e) => applyPromoDiscount(e.detail, e.target));
document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);

(function () {
  function onDOMReady() {
    refreshPromoPricing();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onDOMReady);
  } else {
    onDOMReady();
  }
})();

/* ------------------------------- helpers ---------------------------------- */
function getProductPriceEl({ sourcePromoEl, event } = {}) {
  if (sourcePromoEl) {
    const fromPromo = sourcePromoEl.closest?.('product-price');
    if (fromPromo) return fromPromo;
  }

  const eventProductId = event?.detail?.data?.productId;
  if (eventProductId) {
    const match = document.querySelector(`product-price[data-product-id="${eventProductId}"]`);
    if (match) return match;
  }

  return null;
}

function parseProps(promoEl) {
  if (!promoEl) return {};
  try {
    const json = (promoEl.getAttribute('props') || '{}').replace(/&quot;/g, '"');
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function updateAfterpayAmount(rootEl, priceCents) {
  if (!rootEl) return;
  const major = (Number(priceCents) || 0) / 100;
  const formatted = major.toFixed(2); // keep 2 decimals for Afterpay parsing
  const installment = (major / 4).toFixed(2);

  const applyToPlacement = (placement) => {
    if (!placement) return false;
    placement.setAttribute('data-amount', formatted);
    placement.setAttribute('amount', formatted);
    placement.style.display = 'block';
    ensurePlacementObserver(placement, formatted, installment);
    patchAfterpayShadow(placement, installment);
    return true;
  };

  const setPlacement = () => {
    const placement = rootEl.querySelector('square-placement, square-payment');
    if (!placement) return false;
    return applyToPlacement(placement);
  };

  const ensureObserver = () => {
    const wrapper = rootEl.querySelector('.afterpay-wrapper');
    if (!wrapper || wrapper.__afterpayObserver) return;

    const obs = new MutationObserver(() => {
      const placements = wrapper.querySelectorAll('square-placement, square-payment');
      placements.forEach(applyToPlacement);

      // The placement has its own attribute-filtered observer. The broader
      // child observer is only needed while Afterpay is inserting it.
      if (placements.length) {
        obs.disconnect();
        delete wrapper.__afterpayObserver;
      }
    });
    obs.observe(wrapper, { childList: true, subtree: true });
    wrapper.__afterpayObserver = obs;
  };

  ensureObserver();

  // Run now and re-run a few times to catch delayed widget rendering
  let attempt = 0;
  const trySet = () => {
    if (setPlacement()) {
      // The placement exists now, so the observer waiting for it has nothing left to do.
      const wrapper = rootEl.querySelector('.afterpay-wrapper');
      wrapper?.__afterpayObserver?.disconnect();
      if (wrapper) delete wrapper.__afterpayObserver;
      return;
    }

    if (attempt < 5) {
      attempt += 1;
      setTimeout(trySet, 80);
    }
  };
  trySet();

  const msg = rootEl.querySelector('.afterpay-messaging');
  if (msg) {
    msg.setAttribute('data-amount', formatted);
    msg.style.display = 'block';
    msg.style.marginTop = '5px';
  }

  // Fallback text replacement so the visible message matches the discounted price
  const wrapper = rootEl.querySelector('.afterpay-wrapper');
  if (wrapper) {
    const replaceText = () => {
      const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!node?.textContent) continue;
        if (/\$[0-9]/.test(node.textContent)) {
          node.textContent = node.textContent.replace(/\$[0-9][0-9.,]*/g, `$${installment}`);
        }
      }
    };

    replaceText();
    setTimeout(replaceText, 60);
  }
}

function updatePromoPropsFromDom(rootEl, promoEl) {
  if (!rootEl || !promoEl) return null;

  const priceAmountEl = rootEl.querySelector('.price');
  const comparePriceEl = rootEl.querySelector('.compare-at-price');
  if (!priceAmountEl) return null;

  const nextPrice = parseInt(priceAmountEl.dataset.price, 10) || 0;
  const nextCompare = comparePriceEl ? parseInt(comparePriceEl.dataset.compareAtPrice, 10) || 0 : 0;

  if (!nextPrice) return null;

  const currentProps = parseProps(promoEl);
  const nextProps = {
    ...currentProps,
    productPrice: nextPrice,
    comparePrice: nextCompare || nextPrice,
  };

  promoEl.setAttribute('props', JSON.stringify(nextProps));
  return nextProps;
}

function readDomPrices(rootEl) {
  if (!rootEl) return { priceCents: 0, compareCents: 0 };
  const priceAmountEl = rootEl.querySelector('.price');
  const comparePriceEl = rootEl.querySelector('.compare-at-price');
  const priceCents = priceAmountEl ? parseInt(priceAmountEl.dataset.price, 10) || 0 : 0;
  const compareCents = comparePriceEl ? parseInt(comparePriceEl.dataset.compareAtPrice, 10) || 0 : 0;
  return { priceCents, compareCents };
}

function handleVariantUpdate(event) {
  const rootEl = getProductPriceEl({ event });
  if (!rootEl) return;

  const eventProductId = event?.detail?.data?.productId;
  if (eventProductId && rootEl.dataset.productId && rootEl.dataset.productId !== String(eventProductId)) {
    return; // event for a different product block
  }

  // Defer so the variant update can finish swapping the price DOM first
  requestAnimationFrame(() => {
    const promoEl = rootEl.querySelector('promo-banner');
    if (!promoEl || typeof promoEl.triggerDiscountEvent !== 'function') {
      fadeInPriceContainer(rootEl);
      return;
    }

    const { priceCents, compareCents } = readDomPrices(rootEl);
    if (!priceCents) return;

    const baseKey = `${priceCents}:${compareCents || ''}`;

    const nextProps = updatePromoPropsFromDom(rootEl, promoEl);
    if (!nextProps) return;

    // reset stored promo state so the new variant can calculate fresh
    delete rootEl.dataset.promoOriginalCents;
    delete rootEl.dataset.promoDiscountedCents;
    delete rootEl.dataset.lastPromoKey;
    delete rootEl.dataset.promoBaseKey;
    rootEl.dataset.promoBaseKey = baseKey;

    promoEl.triggerDiscountEvent();
    setTimeout(() => fadeInPriceContainer(rootEl), 400);
  });
}

function resolvePromoValues({ detail = {}, props = {}, rootEl, domPriceCents, domCompareCents }) {
  const storedOriginal = Number(rootEl?.dataset?.promoOriginalCents) || 0;
  const storedDiscounted = Number(rootEl?.dataset?.promoDiscountedCents) || 0;

  const inputOriginal =
    Number.isFinite(detail.originalPrice) ? Number(detail.originalPrice)
    : Number.isFinite(props.comparePrice) ? Number(props.comparePrice)
    : 0;

  const inputDiscounted =
    Number.isFinite(detail.discountedPrice) ? Number(detail.discountedPrice)
    : Number.isFinite(props.productPrice) ? Number(props.productPrice)
    : 0;

  let originalPriceCents =
    inputOriginal || storedOriginal || domCompareCents || domPriceCents;

  let discountedPriceCents =
    inputDiscounted || storedDiscounted || domPriceCents || originalPriceCents;

  // Never let a later call reduce either value once we've stored a promo calculation.
  if (storedOriginal && storedDiscounted) {
    if (originalPriceCents < storedOriginal || discountedPriceCents < storedDiscounted) {
      originalPriceCents = storedOriginal;
      discountedPriceCents = storedDiscounted;
    }
  }

  return {
    originalPriceCents,
    discountedPriceCents,
    // Only persist values that came from promo data (not DOM fallbacks)
    shouldPersist: Boolean(inputOriginal || inputDiscounted),
  };
}

/* ------------------------------- main updater ----------------------------- */
function applyPromoDiscount(detail = {}, sourcePromoEl) {
  const rootEl = getProductPriceEl({ sourcePromoEl });
  if (!rootEl) {
    console.warn('[Pricing] No product-price root found for promo', detail);
    return;
  }

  const promoEl = sourcePromoEl || rootEl.querySelector('promo-banner');
  const propsFromPromo = parseProps(promoEl);
  const money = (cents) => formatCentsIntl(cents, {
    sourceEl: promoEl,
    currency: propsFromPromo.currencyCode,
    locale: propsFromPromo.locale,
  });

  const priceAmountEl = rootEl.querySelector('.price');
  const priceMoneyEl = rootEl.querySelector('.price') || priceAmountEl;
  if (!priceAmountEl || !priceMoneyEl) {
    console.warn('[Pricing] Missing price elements', { priceAmountEl, priceMoneyEl });
    return;
  }

  let comparePriceEl = rootEl.querySelector('.compare-at-price');
  let compareMoneyEl = rootEl.querySelector('.compare-at-price');
  let percentageEl = rootEl.querySelector('.percentage-off');

  const props = propsFromPromo;
  const domPriceCents = parseInt(priceAmountEl.dataset.price, 10) || 0;
  const domCompareCents = comparePriceEl ? parseInt(comparePriceEl.dataset.compareAtPrice, 10) || 0 : 0;
  if (!props.productPrice && !domPriceCents) {
    console.warn('[Pricing] Missing product price in props/DOM', { props, domPriceCents, detail });
  }

  // Decide the "base key" from canonical values (props/detail) so DOM mutations don't trigger re-discount
  // Prefer live DOM values for change detection; props are updated on variant change via handleVariantUpdate
  const basePriceForKey = domPriceCents || Number(props.productPrice) || 0;
  const baseCompareForKey = domCompareCents || Number(props.comparePrice) || 0;
  const baseKey = `${basePriceForKey}:${baseCompareForKey || ''}`;

  // If base values changed (e.g., variant swap), reset stored promo state
  if (rootEl.dataset.promoBaseKey !== baseKey) {
    delete rootEl.dataset.promoOriginalCents;
    delete rootEl.dataset.promoDiscountedCents;
    delete rootEl.dataset.lastPromoKey;
    rootEl.dataset.promoBaseKey = baseKey;
  }

  const { originalPriceCents, discountedPriceCents, shouldPersist } = resolvePromoValues({
    detail,
    props,
    rootEl,
    domPriceCents,
    domCompareCents,
  });

  // Discounted cents come pre-rounded from promo-banner; use as-is to avoid double rounding
  let adjustedDiscountedCents = discountedPriceCents;

  const appliedKey = `${originalPriceCents}:${adjustedDiscountedCents}`;
  const expectedDiscountText = money(adjustedDiscountedCents);
  const isAlreadyRendered =
    rootEl.dataset.lastPromoKey === appliedKey &&
    priceMoneyEl.textContent?.trim() === expectedDiscountText;

  if (isAlreadyRendered) {
    // console.debug('[Pricing] Already rendered promo', { appliedKey, originalPriceCents, adjustedDiscountedCents });
    updateAfterpayAmount(rootEl, adjustedDiscountedCents);
    fadeInPriceContainer(rootEl);
    return;
  }
  rootEl.dataset.lastPromoKey = appliedKey;
  if (shouldPersist) {
    rootEl.dataset.promoOriginalCents = String(originalPriceCents);
    rootEl.dataset.promoDiscountedCents = String(adjustedDiscountedCents);
  }

  if (!originalPriceCents || adjustedDiscountedCents >= originalPriceCents) {
    // console.debug('[Pricing] No discount applied (original <= discounted)', {
    //   originalPriceCents,
    //   adjustedDiscountedCents,
    //   props,
    //   detail,
    //   propsFromPromo,
    // });
    comparePriceEl?.remove();
    percentageEl?.remove();
    priceMoneyEl.textContent = money(originalPriceCents);
    priceMoneyEl.classList.remove('on-sale');
    updateAfterpayAmount(rootEl, originalPriceCents);
    fadeInPriceContainer(rootEl);
    return;
  }

  // hide Yotpo if present
  rootEl.querySelector('.yotpo-product-points-widget')?.style?.setProperty('display', 'none');

  // ensure compare price element exists
  if (!comparePriceEl) {
    comparePriceEl = document.createElement('span');
    comparePriceEl.className = 'compare-at-price';
    comparePriceEl.setAttribute('data-compare-at-price', String(originalPriceCents));

    compareMoneyEl = document.createElement('span');
    compareMoneyEl.className = 'money';
    comparePriceEl.appendChild(compareMoneyEl);

    priceAmountEl.insertAdjacentElement('afterend', comparePriceEl);
  } else if (!compareMoneyEl) {
    compareMoneyEl = document.createElement('span');
    compareMoneyEl.className = 'money';
    comparePriceEl.appendChild(compareMoneyEl);
  }

  compareMoneyEl.textContent = money(originalPriceCents);

  // percentage off
  const calculatedDiscount = Math.round(((originalPriceCents - adjustedDiscountedCents) / originalPriceCents) * 100);
  if (!percentageEl) {
    percentageEl = document.createElement('span');
    percentageEl.className = 'percentage-off';
    comparePriceEl.insertAdjacentElement('afterend', percentageEl);
  }
  percentageEl.textContent = `${calculatedDiscount}% OFF`;

  // update main sale price
  priceMoneyEl.textContent = money(adjustedDiscountedCents);
  priceMoneyEl.classList.add('on-sale');
  updateAfterpayAmount(rootEl, adjustedDiscountedCents);
  renderPromoMessage(rootEl, detail);
  renderFinalSaleBadge(rootEl, props);
  // console.debug('[Pricing] Applied promo', {
  //   originalPriceCents,
  //   adjustedDiscountedCents,
  //   detail,
  //   props,
  //   domPriceCents,
  //   domCompareCents,
  //   baseKey,
  // });
  fadeInPriceContainer(rootEl);
}
function patchAfterpayShadow(placementEl, installmentText) {
  const replaceText = (root) => {
    if (!root) return false;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let changed = false;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node?.textContent) continue;
      if (/\$[0-9]/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/\$[0-9][0-9.,]*/g, `$${installmentText}`);
        changed = true;
      }
    }
    return changed;
  };

  const attemptReplace = (attempt = 0) => {
    const shadow = placementEl.shadowRoot;
    const didReplace = replaceText(shadow);
    if (!didReplace && attempt < 5) {
      setTimeout(() => attemptReplace(attempt + 1), 80);
    }
  };

  attemptReplace();
}

function ensurePlacementObserver(placementEl, formattedAmount, installmentText) {
  if (!placementEl) return;

  const applyIfNeeded = () => {
    let changed = false;
    if (placementEl.getAttribute('data-amount') !== formattedAmount) {
      placementEl.setAttribute('data-amount', formattedAmount);
      changed = true;
    }
    if (placementEl.getAttribute('amount') !== formattedAmount) {
      placementEl.setAttribute('amount', formattedAmount);
      changed = true;
    }
    if (changed) {
      patchAfterpayShadow(placementEl, installmentText);
    }
  };

  placementEl.__afterpayAmountObs?.disconnect();
  const obs = new MutationObserver(() => {
    obs.disconnect();
    applyIfNeeded();
    obs.observe(placementEl, { attributes: true, attributeFilter: ['data-amount', 'amount'] });
  });
  obs.observe(placementEl, { attributes: true, attributeFilter: ['data-amount', 'amount'] });
  placementEl.__afterpayAmountObs = obs;
  applyIfNeeded();
}

function prepPriceContainer(rootEl) {
  const container = rootEl?.querySelector('[ref="priceContainer"]');
  if (!container || container.dataset.fadePrepared) return;
  container.style.opacity = '0';
  container.style.transition = 'opacity 200ms ease';
  container.dataset.fadePrepared = '1';
}

function fadeInPriceContainer(rootEl) {
  const container = rootEl?.querySelector('[ref="priceContainer"]');
  if (!container) return;
  if (!container.dataset.fadePrepared) prepPriceContainer(rootEl);
  requestAnimationFrame(() => {
    container.style.opacity = '1';
    container.classList.add('is-visible');
  });
}

function renderPromoMessage(rootEl, detail = {}) {
  const messageText = `${detail.message || ''} ${detail.discountCode || ''}`.trim();
  const existing = rootEl.querySelector('.promo-msg');

  if (!messageText) {
    existing?.remove();
    return;
  }

  const container = rootEl.querySelector('[ref="priceContainer"]');
  if (!container) return;

  if (existing) {
    if (existing.textContent !== messageText) existing.textContent = messageText;
    if (existing.parentElement !== container) {
      container.appendChild(existing);
    }
    return;
  }

  const msgEl = document.createElement('div');
  msgEl.className = 'promo-msg';
  msgEl.textContent = messageText;

  container.appendChild(msgEl);
}

function renderFinalSaleBadge(rootEl, props = {}) {
  const tags = Array.isArray(props.productTags) ? props.productTags.map((t) => String(t).toLowerCase()) : [];
  const hasFinalSale = tags.some((t) => t.includes('final sale'));
  const container = rootEl.querySelector('[ref="priceContainer"]');
  if (!container) return;

  const percent = container.querySelector('.percentage-off');
  const merchantFinal =
    container.querySelector('.final-sale') ||
    container.querySelector('.percentage-off.final-sale') ||
    (percent && percent.textContent?.trim().toLowerCase() === 'final sale');
  const existingCustom = container.querySelector('.promo-msg.final-sale');

  // If merchant inserted a static final-sale element, leave it and clear our custom badge
  if (merchantFinal) {
    existingCustom?.remove();
    return;
  }

  if (!hasFinalSale) {
    existingCustom?.remove();
    return;
  }

  // Create a simple badge after the percentage (or at end) without overriding merchant markup
  const target = percent || container;
  if (existingCustom) {
    existingCustom.textContent = 'Final Sale';
    if (existingCustom.parentElement !== container || existingCustom.previousElementSibling !== target) {
      target.insertAdjacentElement('afterend', existingCustom);
    }
    return;
  }

  const msgEl = document.createElement('div');
  msgEl.className = 'promo-msg final-sale';
  msgEl.textContent = 'Final Sale';
  target.insertAdjacentElement('afterend', msgEl);
}

function initProductPrice(rootEl) {
  if (!rootEl) return;
  prepPriceContainer(rootEl);

  const { priceCents } = readDomPrices(rootEl);
  if (priceCents) {
    updateAfterpayAmount(rootEl, priceCents);
    setTimeout(() => {
      const { priceCents: laterPrice } = readDomPrices(rootEl);
      if (laterPrice) updateAfterpayAmount(rootEl, laterPrice);
    }, 200);
  }

  const promoEl = rootEl.querySelector('promo-banner');
  if (promoEl?.triggerDiscountEvent) {
    promoEl.triggerDiscountEvent();
    setTimeout(() => fadeInPriceContainer(rootEl), 400);
  } else {
    fadeInPriceContainer(rootEl);
  }
}

function refreshPromoPricing(context = document) {
  context.querySelectorAll('product-price').forEach(initProductPrice);
}

window.refreshPromoPricing = refreshPromoPricing;
